import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { IEmailCampaign, AzavistaApiService, ThemeType } from '@azavista/servicelib';
import { IField, IValueChangedEventArgs, IObjectEnumInputFieldValue, IAzavistaGroupedFieldsComponentData, IValueChangesWithObject } from '@azavista/components/shared';
import { ICreateNewEmailCampaignComponentData } from './interfaces';
import { SharedService, TRANSLATION_MAP, TranslationKey } from '../../shared';

const enum LocalFieldName {
    fromName = 'fromName',
    fromEmail = 'fromEmail',
    emailThemes = 'emailThemes'
}

@Component({
    templateUrl: 'create-new-campaign.component.html',
})
export class CreateNewCampaignComponent implements OnInit {
    fields: IField[] = [];

    emailThemeField: IField;
    emailThemeFieldValue: IObjectEnumInputFieldValue;
    errorsMap: { [key: string]: boolean } = {};
    groupedFields?: IAzavistaGroupedFieldsComponentData;
    isLoaded = false;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: ICreateNewEmailCampaignComponentData,
        private dialogRef: MatDialogRef<CreateNewCampaignComponent, ICreateNewEmailCampaignComponentData>,
        private sharedSvc: SharedService, private apiSvc: AzavistaApiService) {
    }

    async ngOnInit(): Promise<void> {
        this.createFields();
        this.data.campaign = this.data.campaign || { from: {} } as IEmailCampaign;
        await this.loadEmailTemplates();
        this.groupedFields = this.getGroupedFields();
        this.isLoaded = true;
    }

    getGroupedFields(): IAzavistaGroupedFieldsComponentData {
        return {
            editModeForAllFields: true,
            expandPanels: true,
            fieldsWithEntity: {
                fields: this.fields.concat([this.emailThemeField]),
                entity: {
                    [LocalFieldName.emailThemes]: this.emailThemeFieldValue,
                }
            },
            singleColumn: false,
            showActionButtons: false,
            disableAutoOrdering: true,
        };
    }

    async loadEmailTemplates(): Promise<void> {
        this.emailThemeField = {
            name: LocalFieldName.emailThemes, label: this.sharedSvc.translate(TranslationKey.emailThemes),
            editable: true, mandatory_for_planners: true,
            schema: { type: 'object' }, component: 'object_enum'
        } as IField;

        const themes = await this.apiSvc.getAllThemes(ThemeType.email);

        this.emailThemeFieldValue = { displayProperty: 'name', items: themes, selected: themes[0] };
        if (themes && themes[0]) {
            this.data.themeId = themes[0].id;
        }
    }

    onGroupedFieldChanges(value: IValueChangesWithObject) {
        const field = value.changedFieldWithValue.field;
        this.onFieldValueChanged({
            field,
            value: value?.obj?.[field.name],
        });
    }

    onFieldValueChanged(changedArgs: Pick<IValueChangedEventArgs, 'field' | 'value'>): void {
        const fieldName = changedArgs.field.name;
        this.errorsMap[fieldName] = false;
        const value = changedArgs.value;
        if (fieldName === LocalFieldName.fromEmail) {
            this.data.campaign.from.email = value;
        } else if (fieldName === LocalFieldName.fromName) {
            this.data.campaign.from.name = value;
        } else if (fieldName === LocalFieldName.emailThemes) {
            this.data.themeId = value.id;
        } else {
            this.data.campaign[fieldName] = value;
        }
    }

    onFieldInvalidValueSet(changedArgs: IValueChangedEventArgs): void {
        this.errorsMap[changedArgs.field.name] = true;
    }

    onCancel(): void {
        this.dialogRef.close();
    }

    onCreate(): void {
        if (this.hasErrors() || !this.isValid()) {
            return;
        }
        this.dialogRef.close(this.data);
    }

    hasErrors(): boolean {
        const keys = Object.keys(this.errorsMap);
        return keys.some(x => this.errorsMap[x]);
    }

    isValid(): boolean {
        const campaign = this.data.campaign;
        const requiredValues = [
            campaign.bounce_email, campaign.from.email, campaign.from.name, campaign.name, campaign.reply_email, campaign.subject,
            this.data.themeId
        ];
        for (const requiredValue of requiredValues) {
            if (this.sharedSvc.isWhiteSpace(requiredValue)) {
                return false;
            }
        }
        return true;
    }

    createFields(): void {
        this.fields = [{
            name: 'name', label: this.sharedSvc.translate(TranslationKey.internalEmailName),
            schema: { type: 'string' }, editable: true, mandatory_for_planners: true
        }, {
            name: 'subject', label: this.sharedSvc.translate(TranslationKey.emailSubject),
            schema: { type: 'string' }, editable: true, mandatory_for_planners: true
        }, {
            name: LocalFieldName.fromName, label: this.sharedSvc.translate(TranslationKey.fromName),
            schema: { type: 'string' }, editable: true, mandatory_for_planners: true
        }, {
            name: LocalFieldName.fromEmail, label: this.sharedSvc.translate(TranslationKey.fromEmailAddress),
            schema: { type: 'string', format: 'email' }, editable: true, mandatory_for_planners: true
        }, {
            name: 'reply_email', label: this.sharedSvc.translate(TranslationKey.replyEmail),
            schema: { type: 'string', format: 'email' }, editable: true, mandatory_for_planners: true
        }, {
            name: 'bounce_email', label: this.sharedSvc.translate(TranslationKey.bounceEmail),
            schema: { type: 'string', format: 'email' }, editable: true, mandatory_for_planners: true
        }] as IField[];
    }
}
